// client/src/features/equipment/ladder.ts — the organization's own revision ladder · MAINT-006
// (04 §8.9 „the 60/30/14/7 ladder config"; 03 §15.2; 02 §12.2).
//
// 04 asks SCR-EQP-002 to show „the revision timeline with the 60/30/14/7 ladder config", and
// the four numbers in that sentence are NOT a constant this file may hold. `app.notification_rules`
// carries them per organization (03 §15.2), they are seeded — not hard-coded — at signup, and a
// landlord may retune them or switch the family off entirely. D-942 records the server-side
// version of this exact mistake: a producer with 60/30/14/7 built in „would quietly outvote a
// landlord who switched the family off". A client that drew the ladder from a constant would do
// the same thing, one layer up and in a place the landlord can see.
//
// So the ladder is READ, from `GET /api/v1/notification-rules`, and the `REVISION_DUE` row is
// what the timeline draws. Nothing here has a fallback set of offsets: when the row cannot be
// read the timeline says so and shows the recorded dates alone (see `known` below).
//
// THE READ IS GATED, and this is the one wrinkle worth naming. That endpoint is behind
// `Notifications.ManageRules`, which the seeded matrix grants to `owner` and `manager` — the
// same two roles that hold `Operations.Read` and can therefore open this screen at all; the
// `accountant` holds neither. The query is nonetheless `enabled` on the permission rather than
// fired blind, so a narrowed membership that gains `Operations.Read` without the rules meets a
// timeline with a missing ladder instead of a 403 the screen would have to render as an error.
// See DEVIATIONS D-1101.

import { useQuery } from '@tanstack/react-query';

import type { NotificationPriority } from '@/client';
import { listNotificationRulesOptions } from '@/client/@tanstack/react-query.gen';
import { wireNumber, wireNumberOrNull } from '@/lib/wire';
import { useCan } from '@/session';

import { addDays } from './dates';

/** 03 §15.2's registry value for the equipment family — `NotificationRuleCatalog.RevisionDue`. */
export const REVISION_RULE_TYPE = 'REVISION_DUE';

/** The policy family, read by SCR-PROP-003's „Asigurări" slot — `InsuranceExpiry`. */
export const INSURANCE_RULE_TYPE = 'INSURANCE_EXPIRY';

export interface RevisionLadder {
  /** The rungs, widest first, as the organization has them. Empty when the family has none. */
  offsets: number[];
  /** Whether the family fires at all. A disabled family reaches no rung (`ComplianceLadder`). */
  enabled: boolean;
  /** How often the post-expiry escalation repeats, or null when the row does not say. */
  postExpiryRepeatDays: number | null;
  /** False while the rule row has not been read — no permission, still loading, or an error. */
  known: boolean;
}

const UNKNOWN: RevisionLadder = {
  offsets: [],
  enabled: false,
  postExpiryRepeatDays: null,
  known: false,
};

/**
 * One family's ladder, as the organization has it.
 *
 * `ruleType` is a parameter because two screens need two families out of the SAME response —
 * SCR-EQP-002 reads `REVISION_DUE` and SCR-PROP-003's „Asigurări" slot reads `INSURANCE_EXPIRY`
 * — and both are rows of one `GET /api/v1/notification-rules`. The two hooks below name their
 * family so a call site cannot colour a policy with the equipment's rungs by accident; the
 * query itself is cached once and shared.
 */
export function useComplianceLadder(ruleType: string): RevisionLadder {
  const canReadRules = useCan('Notifications.ManageRules');
  const rules = useQuery({ ...listNotificationRulesOptions(), enabled: canReadRules });

  if (!canReadRules || rules.data === undefined) return UNKNOWN;

  const rule = rules.data.find((row) => row.ruleType === ruleType);
  if (rule === undefined) return UNKNOWN;

  return {
    // Widest first, which is the order a countdown is read in and NOT the order the column is
    // stored in — `alert_offsets_days` is an int[] nobody promised to sort.
    offsets: [...rule.alertOffsetsDays.map(wireNumber)]
      .filter((offset) => offset > 0)
      .sort((left, right) => right - left),
    enabled: rule.enabled,
    postExpiryRepeatDays: wireNumberOrNull(rule.postExpiryRepeatDays),
    known: true,
  };
}

/** The equipment family — SCR-EQP-001's chips and SCR-EQP-002's timeline. */
export function useRevisionLadder(): RevisionLadder {
  return useComplianceLadder(REVISION_RULE_TYPE);
}

/** The policy family — SCR-PROP-003's „Asigurări" slot. */
export function useInsuranceLadder(): RevisionLadder {
  return useComplianceLadder(INSURANCE_RULE_TYPE);
}

/** The widest rung — how far out the ladder starts counting (`ComplianceLadder.Horizon`). */
export function horizonOf(ladder: RevisionLadder): number {
  return ladder.enabled ? (ladder.offsets[0] ?? 0) : 0;
}

/**
 * The colour a scadență is worth, or null for one nothing has to say about yet.
 *
 * 01 BR-NOTIF-02 rows 6 and 7 as `NotificationPriorityPolicy.ForCompliance` states them:
 * IMPORTANT while the deadline is ahead, CRITICAL once it has passed. A deadline further out
 * than the widest rung gets no chip at all, because the ladder has not reached it and a screen
 * that coloured it would be alarming a landlord about a revision due in a year.
 *
 * `daysUntil` is the SERVER's `daysUntilRevision`, never a difference computed here.
 */
export function revisionPriority(
  daysUntil: number | null,
  ladder: RevisionLadder,
): NotificationPriority | null {
  if (daysUntil === null) return null;
  if (daysUntil < 0) return 'CRITICAL';
  return ladder.enabled && daysUntil <= horizonOf(ladder) ? 'IMPORTANT' : null;
}

/** One rung of the timeline: the day it fires and whether that day has come. */
export interface LadderMilestone {
  /** `D60`, `D30` … — 02 §12.3's milestone segment, and the row's React key. */
  key: string;
  daysBefore: number;
  /** The scadență minus `daysBefore`, as a DateOnly. */
  date: string;
  /** Whether the rung's day is behind us. */
  past: boolean;
}

/**
 * The ladder laid out against one scadență: a date per rung, widest first.
 *
 * Every date here is DERIVED — scadență minus offset — and both inputs are the server's: the
 * scadență is `equipment.nextRevisionDate` (03 §11.7 makes it a stored column no client may
 * send) and the offsets are the organization's rule row. What is NOT derived is which rung
 * actually fired: that is `ComplianceLadderScanner`'s decision, it lives in
 * `app.maintenance_tasks`, and no endpoint on this line publishes those rows (D-1102).
 */
export function ladderMilestones(
  nextRevisionDate: string,
  ladder: RevisionLadder,
  today: string,
): LadderMilestone[] {
  if (!ladder.enabled) return [];

  const milestones: LadderMilestone[] = [];

  for (const daysBefore of ladder.offsets) {
    const date = addDays(nextRevisionDate, -daysBefore);
    if (date === null) continue;
    milestones.push({ key: `D${daysBefore}`, daysBefore, date, past: date <= today });
  }

  return milestones;
}
